import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EntraConnectStart, EntraGroupSearchResult, EntraMapping, EntraRun } from '@cloudsign/shared';
import type { EntraStatus } from '@cloudsign/shared';
import { Alert, Button, Card, ConfirmButton, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { StatusPill } from '../../components/phase2';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useGroups } from '../people/api';
import { entraKey, mappingsKey, runsKey, useEntraStatus, useMappings, useRuns } from './api';

const RUN_TONE = { ok: 'good', partial: 'warn', failed: 'bad', stopped: 'warn', running: 'info' } as const;

/** Microsoft 365 directory sync (S47b server). Connect, link groups, sync or preview, and recent runs. */
export function EntraCard({ canManage }: { canManage: boolean }) {
  const status = useEntraStatus();
  const connected = status.data?.connected ?? false;
  const mappings = useMappings(connected);
  const runs = useRuns(connected);
  const qc = useQueryClient();
  const toast = useToast();
  const connect = useMutation({
    mutationFn: () => api.post('/integrations/entra/connect', { schema: EntraConnectStart }),
    onSuccess: (r) => window.location.assign(r.url),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post('/integrations/entra/disconnect'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: entraKey }),
  });
  const sync = useMutation({
    mutationFn: (preview: boolean) => api.post('/integrations/entra/sync', { body: { preview }, schema: EntraRun }).then(() => preview),
    onSuccess: (preview) => {
      toast(preview ? t('p4w27.m365.previewStarted') : t('p4w27.m365.started'));
      void qc.invalidateQueries({ queryKey: runsKey });
      void qc.invalidateQueries({ queryKey: entraKey });
    },
  });
  return (
    <Card title={t('p4w27.m365.title')} description={t('p4w27.m365.description')}>
      {status.isError ? <ErrorPanel error={status.error} onRetry={() => void status.refetch()} /> : null}
      {!status.data && !status.isError ? <Spinner /> : null}
      {status.data ? (
        !status.data.available ? (
          <Alert kind="info">{t('p4w27.m365.unavailable')}</Alert>
        ) : (
          <div className="space-y-6">
            <StatusBlock status={status.data} />
            {canManage ? (
              <div className="flex flex-wrap gap-2">
                {!status.data.connected ? (
                  <Button variant="primary" disabled={connect.isPending} onClick={() => connect.mutate()}>
                    {t('p4w27.m365.connect')}
                  </Button>
                ) : (
                  <>
                    <Button variant="primary" disabled={sync.isPending} onClick={() => sync.mutate(false)}>
                      {t('p4w27.m365.syncNow')}
                    </Button>
                    <Button disabled={sync.isPending} onClick={() => sync.mutate(true)}>
                      {t('p4w27.m365.preview')}
                    </Button>
                    <ConfirmButton label={t('p4w27.m365.disconnect')} confirmLabel={t('p4w27.m365.disconnectConfirm')} pending={disconnect.isPending} onConfirm={() => disconnect.mutate()} />
                  </>
                )}
              </div>
            ) : null}
            {canManage && !status.data.connected ? <p className="text-sm text-slate-600">{t('sso.connect.signInHint')}</p> : null}
            <ErrorLine error={connect.error ?? disconnect.error ?? sync.error} />
            {status.data.connected ? (
              <>
                <MappingsSection loading={mappings.isLoading} error={mappings.error} items={mappings.data ?? []} canManage={canManage} />
                <RunsSection loading={runs.isLoading} error={runs.error} items={runs.data?.items ?? []} />
              </>
            ) : null}
          </div>
        )
      ) : null}
    </Card>
  );
}

function StatusBlock({ status }: { status: EntraStatus }) {
  return (
    <div className="space-y-1 text-sm" aria-live="polite">
      <p>{status.connected && status.connectedAt ? t('p4w27.m365.connected', { when: formatDateTime(status.connectedAt) }) : t('p4w27.m365.notConnected')}</p>
      {status.connected ? (
        <p className="flex flex-wrap items-center gap-2 text-slate-600">
          {status.lastRunAt ? t('p4w27.m365.lastRun', { when: formatDateTime(status.lastRunAt) }) : t('p4w27.m365.neverRun')}
          {status.lastRunStatus ? <StatusPill tone={RUN_TONE[status.lastRunStatus]}>{t(`p4w27.m365.runStatus.${status.lastRunStatus}`)}</StatusPill> : null}
        </p>
      ) : null}
    </div>
  );
}

function MappingsSection({ items, loading, error, canManage }: { items: EntraMapping[]; loading: boolean; error: unknown; canManage: boolean }) {
  const groups = useGroups();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: mappingsKey });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.patch(`/integrations/entra/mappings/${v.id}`, { body: v.body, schema: EntraMapping }),
    onSuccess: () => void refresh(),
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/integrations/entra/mappings/${id}`), onSuccess: () => void refresh() });
  const groupName = (id: string) => groups.data?.find((g) => g.id === id)?.name ?? id;
  return (
    <section aria-labelledby="m365-mappings" className="space-y-3">
      <h3 id="m365-mappings" className="text-sm font-semibold text-slate-900">
        {t('p4w27.m365.mappings')}
      </h3>
      {error ? <ErrorPanel error={error} /> : null}
      {loading ? <Spinner /> : null}
      {!loading && !error && items.length === 0 ? <p className="text-sm text-slate-600">{t('p4w27.m365.mappingsEmpty')}</p> : null}
      {items.length > 0 ? (
        <TableWrap caption={t('p4w27.m365.mappingsLabel')}>
          <thead>
            <tr>
              <Th>{t('p4w27.m365.entraGroup')}</Th>
              <Th>{t('p4w27.m365.cloudGroup')}</Th>
              <Th>{t('p4w27.m365.phoneSource')}</Th>
              <Th>{t('p4w27.m365.archiveMissing')}</Th>
              <Th>{t('p4w27.m365.enabled')}</Th>
              <Th>
                <span className="sr-only">{t('common.actions')}</span>
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((m) => (
              <tr key={m.id}>
                <td className="px-3 py-2 font-medium">{m.entraGroupName}</td>
                <td className="px-3 py-2">{groupName(m.groupId)}</td>
                <td className="px-3 py-2">{t(`p4w27.m365.phone.${m.phoneSource}`)}</td>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={`${t('p4w27.m365.archiveMissing')}: ${m.entraGroupName}`}
                    checked={m.archiveMissing}
                    disabled={!canManage || patch.isPending}
                    onChange={(e) => patch.mutate({ id: m.id, body: { archiveMissing: e.target.checked } })}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={`${t('p4w27.m365.enabled')}: ${m.entraGroupName}`}
                    checked={m.enabled}
                    disabled={!canManage || patch.isPending}
                    onChange={(e) => patch.mutate({ id: m.id, body: { enabled: e.target.checked } })}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  {canManage ? <ConfirmButton label={t('p4w27.m365.remove')} confirmLabel={t('p4w27.m365.removeConfirm')} pending={remove.isPending} onConfirm={() => remove.mutate(m.id)} /> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : null}
      <ErrorLine error={patch.error ?? remove.error} />
      {canManage ? <AddMapping onAdded={() => void refresh()} /> : null}
    </section>
  );
}

function AddMapping({ onAdded }: { onAdded: () => void }) {
  const groups = useGroups();
  const repeat = (groups.data ?? []).filter((g) => g.kind === 'repeat');
  const [q, setQ] = useState('');
  const [groupId, setGroupId] = useState('');
  const [phone, setPhone] = useState<'mobilePhone' | 'businessPhones' | 'none'>('mobilePhone');
  const search = useMutation({ mutationFn: () => api.get('/integrations/entra/groups', { query: { q: q.trim() }, schema: EntraGroupSearchResult }) });
  const link = useMutation({
    mutationFn: (g: { id: string; displayName: string }) =>
      api.post('/integrations/entra/mappings', { body: { entraGroupId: g.id, entraGroupName: g.displayName, groupId: groupId || repeat[0]?.id, phoneSource: phone, archiveMissing: true }, schema: EntraMapping }),
    onSuccess: onAdded,
  });
  const target = groupId || repeat[0]?.id || '';
  return (
    <form
      className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) search.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField label={t('p4w27.m365.cloudGroup')} value={target} onChange={(e) => setGroupId(e.target.value)}>
          <option value="">{t('p4w27.m365.pickGroup')}</option>
          {repeat.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('p4w27.m365.phoneSource')} value={phone} onChange={(e) => setPhone(e.target.value as typeof phone)}>
          {(['mobilePhone', 'businessPhones', 'none'] as const).map((p) => (
            <option key={p} value={p}>
              {t(`p4w27.m365.phone.${p}`)}
            </option>
          ))}
        </SelectField>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <TextField className="min-w-0 flex-1" label={t('p4w27.m365.search')} value={q} maxLength={100} onChange={(e) => setQ(e.target.value)} />
        <Button type="submit" disabled={!q.trim() || search.isPending}>
          {t('p4w27.m365.searchButton')}
        </Button>
      </div>
      <div aria-live="polite">
        {search.data && search.data.items.length === 0 ? <p className="text-sm text-slate-600">{t('p4w27.m365.searchNone')}</p> : null}
        <ul className="divide-y divide-slate-100">
          {(search.data?.items ?? []).map((g) => (
            <li key={g.id} className="flex items-center justify-between gap-3 py-2">
              <span className="text-sm">
                {g.displayName}
                {g.memberCount !== null ? <span className="text-slate-600"> ({t('p4w27.m365.members', { count: g.memberCount })})</span> : null}
              </span>
              <Button aria-label={t('p4w27.m365.linkLabel', { name: g.displayName })} disabled={!target || link.isPending} onClick={() => link.mutate(g)}>
                {t('p4w27.m365.link')}
              </Button>
            </li>
          ))}
        </ul>
      </div>
      <ErrorLine error={search.error ?? link.error} />
    </form>
  );
}

function RunsSection({ items, loading, error }: { items: EntraRun[]; loading: boolean; error: unknown }) {
  return (
    <section aria-labelledby="m365-runs" className="space-y-3">
      <h3 id="m365-runs" className="text-sm font-semibold text-slate-900">
        {t('p4w27.m365.runs')}
      </h3>
      {error ? <ErrorPanel error={error} /> : null}
      {loading ? <Spinner /> : null}
      {!loading && !error && items.length === 0 ? <p className="text-sm text-slate-600">{t('p4w27.m365.runsEmpty')}</p> : null}
      {items.length > 0 ? (
        <TableWrap caption={t('p4w27.m365.runsLabel')}>
          <thead>
            <tr>
              <Th>{t('p4w27.m365.run.started')}</Th>
              <Th>{t('p4w27.m365.run.kind')}</Th>
              <Th>{t('p4w27.m365.run.result')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 align-top">{formatDateTime(r.startedAt)}</td>
                <td className="px-3 py-2 align-top">{t(`p4w27.m365.run.trigger.${r.trigger}`)}</td>
                <td className="px-3 py-2 align-top">
                  <StatusPill tone={RUN_TONE[r.status]}>{t(`p4w27.m365.runStatus.${r.status}`)}</StatusPill>
                  <p className="mt-1 text-xs text-slate-600">{t('p4w27.m365.run.counts', { created: r.created, updated: r.updated, archived: r.archived, restored: r.restored, skipped: r.skipped })}</p>
                  {r.problems.map((p) => (
                    <p key={p.key} className="text-xs text-slate-600">
                      {t('p4w27.m365.run.problem', { count: p.count, problem: t(p.key) })}
                    </p>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : null}
    </section>
  );
}
